import type { CSSProperties } from "react";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import {
  ASSET_BASE,
  type BorderSide,
  classNames,
  extractUtilityClasses,
  getBorderClasses,
  sanitizeImageSrc,
  toCssLength,
  useAppBarTransparent,
} from "./shared";

export function ImageChild({
  isVisible = true,
  src,
  transparentSrc,
  alt = "",
  width,
  height,
  aspectRatio,
  objectFit,
  objectPosition,
  align = "center",
  rounded,
  padding,
  border = false,
  borderColor,
  borderWidth,
  borderStyle,
  borderSides,
}: {
  isVisible?: boolean;
  src?: string;
  transparentSrc?: string;
  alt?: string;
  width?: string;
  height?: string;
  aspectRatio?: string;
  objectFit?: string;
  objectPosition?: string;
  align?: "left" | "center" | "right";
  rounded?: string;
  padding?: string;
  border?: boolean;
  borderColor?: string;
  borderWidth?: string;
  borderStyle?: string;
  borderSides?: BorderSide[];
}) {
  const { currentRounding, currentColorPalette } = useWBTheme();
  const isTransparentActive = useAppBarTransparent();
  const widthClasses = extractUtilityClasses(width, "w-");
  const heightClasses = extractUtilityClasses(height, "h-");
  const fitClasses = extractUtilityClasses(objectFit, "object-");
  const positionClasses = extractUtilityClasses(objectPosition, "object-");
  const roundedClasses = extractUtilityClasses(
    rounded ?? currentRounding,
    "rounded",
  );
  const aspectClasses = extractUtilityClasses(aspectRatio, "aspect-");
  const borderClasses = getBorderClasses(border, borderWidth || "border", borderStyle || "border-solid", borderSides);
  const isCustom = aspectRatio === "custom";
  const hasFixedHeight = height !== undefined && height !== "h-auto";
  if (!isVisible) return null;
  const resolvedSrc = src === undefined ? `${ASSET_BASE}/shared/events01.jpg` : src;
  const sanitizedSrc = sanitizeImageSrc(resolvedSrc);
  if (!sanitizedSrc) return null;
  const sanitizedTransparentSrc = transparentSrc
    ? sanitizeImageSrc(transparentSrc)
    : undefined;

  const fitStyle: CSSProperties = {
    objectFit: fitClasses
      ? undefined
      : (objectFit as CSSProperties["objectFit"] | undefined),
    objectPosition: positionClasses
      ? undefined
      : (objectPosition as CSSProperties["objectPosition"] | undefined),
  };

  if (isCustom || aspectRatio) {
    const sizeClasses = isCustom
      ? classNames(widthClasses, heightClasses)
      : hasFixedHeight
        ? classNames(heightClasses, aspectClasses)
        : classNames("w-full", aspectClasses);
    return (
      <div
        className={
          isCustom || hasFixedHeight
            ? classNames(
                "w-full flex",
                align === "left"
                  ? "justify-start"
                  : align === "right"
                    ? "justify-end"
                    : "justify-center",
              )
            : "w-full"
        }
      >
        <div
          className={classNames(
            sizeClasses,
            padding,
            borderClasses,
            roundedClasses,
            "relative overflow-hidden",
          )}
          style={{
            borderRadius: roundedClasses
              ? undefined
              : toCssLength(rounded ?? currentRounding),
            borderColor: border ? (borderColor ?? currentColorPalette.secondary) : undefined,
          }}
        >
          <img
            className={classNames(
              "w-full h-full",
              fitClasses ?? "object-cover",
              positionClasses ?? "object-center",
            )}
            src={sanitizedSrc}
            alt={alt}
            style={{
              ...fitStyle,
              ...(sanitizedTransparentSrc
                ? {
                    opacity: isTransparentActive ? 0 : 1,
                    transition: "opacity 0.3s ease",
                  }
                : {}),
            }}
          />
          {sanitizedTransparentSrc && (
            <img
              className={classNames(
                "absolute inset-0 w-full h-full",
                fitClasses ?? "object-cover",
                positionClasses ?? "object-center",
              )}
              src={sanitizedTransparentSrc}
              alt={alt}
              style={{
                ...fitStyle,
                opacity: isTransparentActive ? 1 : 0,
                transition: "opacity 0.3s ease",
              }}
            />
          )}
        </div>
      </div>
    );
  }

  if (sanitizedTransparentSrc) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent:
            align === "right"
              ? "flex-end"
              : align === "left"
                ? "flex-start"
                : "center",
          width: "100%",
        }}
      >
        <div
          className={classNames(padding, borderClasses, roundedClasses, "relative overflow-hidden")}
          style={{
            display: "inline-flex",
            borderRadius: roundedClasses
              ? undefined
              : toCssLength(rounded ?? currentRounding),
            borderColor: border ? (borderColor ?? currentColorPalette.secondary) : undefined,
          }}
        >
          <img
            className={classNames(
              widthClasses,
              heightClasses,
              fitClasses,
              positionClasses,
            )}
            src={sanitizedSrc}
            alt={alt}
            style={{
              width: widthClasses ? undefined : toCssLength(width),
              height: heightClasses ? undefined : toCssLength(height),
              ...fitStyle,
              opacity: isTransparentActive ? 0 : 1,
              transition: "opacity 0.3s ease",
            }}
          />
          <img
            className={classNames(
              "absolute inset-0 w-full h-full",
              fitClasses,
              positionClasses,
            )}
            src={sanitizedTransparentSrc}
            alt={alt}
            style={{
              ...fitStyle,
              opacity: isTransparentActive ? 1 : 0,
              transition: "opacity 0.3s ease",
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        justifyContent:
          align === "right"
            ? "flex-end"
            : align === "left"
              ? "flex-start"
              : "center",
        width: "100%",
      }}
    >
      <img
        className={classNames(
          widthClasses,
          heightClasses,
          fitClasses,
          positionClasses,
          padding,
          borderClasses,
          roundedClasses,
        )}
        src={sanitizedSrc}
        alt={alt}
        style={{
          width: widthClasses ? undefined : toCssLength(width),
          height: heightClasses ? undefined : toCssLength(height),
          borderRadius: roundedClasses
            ? undefined
            : toCssLength(rounded ?? currentRounding),
          borderColor: border ? (borderColor ?? currentColorPalette.secondary) : undefined,
          ...fitStyle,
        }}
      />
    </div>
  );
}
